/**
 * Organization Remittances Section
 *
 * Admin view for browsing ERA 835 remittance payments for an organization.
 * Shows parsed payment/adjudication data at three levels:
 * Remittance (header) → Claims → Service Lines.
 *
 * Uses the generated hooks from store/generated/billing.ts.
 */

import { useCallback, useState } from 'react';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  RemittanceClaimResponse2,
  RemittanceHeaderResponse,
  RemittanceLineResponse2,
} from '@store/generated/billing';
import { enhancedApi as billingApi } from '@store/generated/billing';

import { formatDateTime,formatRelativeTime } from '../utils/formatting';

// ============================================================================
// Types (for loosely-typed adjustment/remark fields)
// ============================================================================

interface AdjustmentEntry {
  groupCode?: string;
  reasonCode?: string;
  reasonDescription?: string;
  amountCents?: number;
}

interface RemarkCodeEntry {
  remarkCode?: string;
  remark?: string;
  codeListQualifierCode?: string;
}

// ============================================================================
// Constants
// ============================================================================

const CLAIM_STATUS_LABELS: Record<string, { label: string; color: 'success' | 'info' | 'error' | 'warning' | 'default' }> = {
  '1': { label: 'Processed (Primary)', color: 'success' },
  '2': { label: 'Processed (Secondary)', color: 'info' },
  '4': { label: 'Denied', color: 'error' },
  '22': { label: 'Reversal', color: 'warning' },
};

const ADJUSTMENT_GROUP_LABELS: Record<string, string> = {
  'CO': 'Contractual Write-off',
  'PR': 'Patient Responsibility',
  'OA': 'Other Adjustment',
  'PI': 'Payor Initiated',
  'CR': 'Corrections/Reversals',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  'CHK': 'Check',
  'ACH': 'Electronic (ACH)',
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ============================================================================
// Helpers
// ============================================================================

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function getClaimStatusDisplay(code: string): { label: string; color: 'success' | 'info' | 'error' | 'warning' | 'default' } {
  return CLAIM_STATUS_LABELS[code] ?? { label: `Status ${code}`, color: 'default' };
}

function getPaymentMethodLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return PAYMENT_METHOD_LABELS[code] ?? code;
}

function getAdjustmentGroupLabel(code: string): string {
  return ADJUSTMENT_GROUP_LABELS[code] ?? code;
}

// ============================================================================
// Sub-Components
// ============================================================================

interface ClaimAccordionProps {
  claim: RemittanceClaimResponse2;
  isExpanded: boolean;
  onToggle: () => void;
}

function ClaimAccordion({ claim, isExpanded, onToggle }: ClaimAccordionProps) {
  const theme = useTheme();
  const status = getClaimStatusDisplay(claim.claim_status_code);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 1.5,
      }}
    >
      {/* Claim header — always visible, clickable */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.palette.grey[50] },
        }}
        onClick={onToggle}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              PCN: {claim.patient_control_number}
            </Typography>
            {claim.claim_id ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                Claim: {claim.claim_id}
              </Typography>
            ) : (
              <Chip label="Unmatched" size="small" variant="outlined" color="warning" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Billed: {formatCents(claim.total_claim_charge_amount_cents)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Paid: {formatCents(claim.claim_payment_amount_cents)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Patient: {formatCents(claim.patient_responsibility_amount_cents)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <Chip label={status.label} size="small" color={status.color} />
          {claim.forwarded_to_secondary && (
            <Chip label="Fwd to Secondary" size="small" variant="outlined" color="info" />
          )}
          <ExpandMoreIcon
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: '0.2s',
              color: theme.palette.text.secondary,
            }}
          />
        </Box>
      </Box>

      {/* Expanded detail */}
      <Collapse in={isExpanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {/* Claim metadata */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {claim.payer_claim_control_number && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Payer Claim Ref</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                  {claim.payer_claim_control_number}
                </Typography>
              </Grid>
            )}
            {claim.rendering_provider_npi && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Rendering NPI</Typography>
                <Typography variant="body2">{claim.rendering_provider_npi}</Typography>
              </Grid>
            )}
            {(claim.statement_period_start || claim.statement_period_end) && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Statement Period</Typography>
                <Typography variant="body2">
                  {claim.statement_period_start ?? '—'} – {claim.statement_period_end ?? '—'}
                </Typography>
              </Grid>
            )}
            {claim.claim_received_date && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Received</Typography>
                <Typography variant="body2">{claim.claim_received_date}</Typography>
              </Grid>
            )}
          </Grid>

          {/* Claim-level adjustments */}
          {claim.claim_adjustments && claim.claim_adjustments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Claim Adjustments
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Group</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claim.claim_adjustments.map((adj, i) => {
                    const a = adj as AdjustmentEntry;
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip
                            label={a.groupCode ?? '—'}
                            size="small"
                            variant="outlined"
                            title={a.groupCode ? getAdjustmentGroupLabel(a.groupCode) : undefined}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                          {a.reasonCode ?? '—'}
                        </TableCell>
                        <TableCell>{a.reasonDescription ?? '—'}</TableCell>
                        <TableCell align="right">{formatCents(a.amountCents)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Service lines */}
          {claim.lines && claim.lines.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Service Lines
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Procedure</TableCell>
                      <TableCell>Modifiers</TableCell>
                      <TableCell>Service Date</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Paid</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {claim.lines.map((line) => (
                      <ServiceLineRows key={line.remittance_line_id} line={line} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

interface ServiceLineRowsProps {
  line: RemittanceLineResponse2;
}

function ServiceLineRows({ line }: ServiceLineRowsProps) {
  const theme = useTheme();
  const adjustments = (line.service_adjustments ?? []) as AdjustmentEntry[];
  const remarks = (line.remark_codes ?? []) as RemarkCodeEntry[];
  const codeChanged = line.submitted_procedure_code && line.submitted_procedure_code !== line.adjudicated_procedure_code;

  return (
    <>
      {/* Main line row */}
      <TableRow>
        <TableCell>
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 500 }}>
              {line.adjudicated_procedure_code ?? '—'}
            </Typography>
            {codeChanged && (
              <Typography variant="caption" color="text.secondary">
                Submitted: {line.submitted_procedure_code}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {line.adjudicated_procedure_modifier_codes && line.adjudicated_procedure_modifier_codes.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {line.adjudicated_procedure_modifier_codes.map((mod, i) => (
                <Chip key={i} label={mod} size="small" variant="outlined" />
              ))}
            </Box>
          ) : '—'}
        </TableCell>
        <TableCell>{line.service_date ?? '—'}</TableCell>
        <TableCell align="right">{formatCents(line.line_item_charge_amount_cents)}</TableCell>
        <TableCell align="right">{formatCents(line.line_item_payment_amount_cents)}</TableCell>
      </TableRow>

      {/* Line-level adjustments sub-rows */}
      {adjustments.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0.5, pl: 4, bgcolor: theme.palette.grey[50], borderBottom: 'none' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {adjustments.map((adj, i) => (
                <Typography key={i} variant="caption" color="text.secondary">
                  <Chip
                    label={adj.groupCode ?? '?'}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 0.5, height: 18, fontSize: '0.6875rem' }}
                  />
                  {adj.reasonCode} — {adj.reasonDescription ?? 'Unknown'}: {formatCents(adj.amountCents)}
                </Typography>
              ))}
            </Box>
          </TableCell>
        </TableRow>
      )}

      {/* Remark codes sub-row */}
      {remarks.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0.5, pl: 4, bgcolor: theme.palette.grey[50] }}>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Remarks:
              </Typography>
              {remarks.map((r, i) => (
                <Tooltip key={i} title={r.remark ?? ''}>
                  <Chip label={r.remarkCode ?? '?'} size="small" variant="outlined" />
                </Tooltip>
              ))}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ============================================================================
// Detail Dialog
// ============================================================================

interface RemittanceDetailDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  remittanceId: string | null;
}

function RemittanceDetailDialog({ open, onClose, organizationId, remittanceId }: RemittanceDetailDialogProps) {
  const theme = useTheme();
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);

  const { data, isLoading, error } = billingApi.useGetV2AdminOrgsByOrganizationIdRemittancesAndRemittanceIdQuery(
    { organizationId, remittanceId: remittanceId ?? '' },
    { skip: !remittanceId || !open },
  );

  const remittance = data?.remittance;
  const claims = data?.claims ?? [];

  const handleToggleClaim = useCallback((claimId: string) => {
    setExpandedClaimId(prev => (prev === claimId ? null : claimId));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Remittance Detail</span>
          <IconButton edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            {extractApiErrorMessage(error, 'Failed to load remittance details')}
          </Alert>
        )}

        {remittance && (
          <Box>
            {/* Header Summary */}
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
                mb: 3,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}
              >
                {remittance.check_or_eft_trace_number}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {remittance.payer_name ?? 'Unknown Payer'}
                </Typography>
                <Chip
                  label={getPaymentMethodLabel(remittance.payment_method_code)}
                  size="small"
                  variant="outlined"
                />
                {remittance.application_mode && (
                  <Chip
                    label={remittance.application_mode}
                    size="small"
                    variant="outlined"
                    color={remittance.application_mode === 'production' ? 'primary' : 'default'}
                  />
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCents(
                      remittance.total_payment_amount_cents ??
                        (claims.reduce((sum, c) => sum + (c.claim_payment_amount_cents ?? 0), 0) || null)
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Claims</Typography>
                  <Typography variant="body2">{claims.length}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Received</Typography>
                  <Typography variant="body2">
                    {remittance.created_at ? formatDateTime(remittance.created_at) : '—'}
                  </Typography>
                </Grid>
                {remittance.stedi_transaction_id && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Stedi TX</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                      {remittance.stedi_transaction_id}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Claims */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Claims ({claims.length})
            </Typography>

            {claims.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {claims.map(claim => (
                  <ClaimAccordion
                    key={claim.remittance_claim_id}
                    claim={claim}
                    isExpanded={expandedClaimId === claim.remittance_claim_id}
                    onToggle={() => handleToggleClaim(claim.remittance_claim_id)}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                <Typography variant="body2">No claim details available for this remittance.</Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface OrganizationRemittancesSectionProps {
  organizationId: string;
  isConnected: boolean;
}

export function OrganizationRemittancesSection({ organizationId, isConnected }: OrganizationRemittancesSectionProps) {
  const theme = useTheme();

  // State
  const [perPage, setPerPage] = useState(25);
  const [selectedRemittanceId, setSelectedRemittanceId] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Query
  const { data, isLoading, isFetching, error, refetch } = billingApi.useGetV2AdminOrgsByOrganizationIdRemittancesQuery(
    { organizationId, perPage },
    { skip: !isConnected },
  );

  const remittances = data?.items ?? [];
  const total = data?.total ?? 0;

  // Handlers
  const handleRowClick = useCallback((remittance: RemittanceHeaderResponse) => {
    setSelectedRemittanceId(remittance.remittance_id);
    setDetailDialogOpen(true);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setPerPage(newLimit);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Not connected state
  if (!isConnected) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          Connect to Stedi first to view remittances.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              ERA 835 Remittances
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <InputLabel>Limit</InputLabel>
                <Select
                  value={perPage}
                  label="Limit"
                  onChange={e => handleLimitChange(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={isFetching}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Remittances Table */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <CardContent>
          {/* Loading state */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error state */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {extractApiErrorMessage(error, 'Failed to load remittances')}
            </Alert>
          )}

          {/* Table */}
          {!isLoading && !error && (
            <>
              {remittances.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Trace #</TableCell>
                        <TableCell>Payer</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell>Received</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {remittances.map(rem => (
                        <TableRow
                          key={rem.remittance_id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(rem)}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 500 }}>
                              {rem.check_or_eft_trace_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {rem.payer_name ?? 'Unknown Payer'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getPaymentMethodLabel(rem.payment_method_code)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={rem.application_mode ?? '—'}
                              size="small"
                              variant="outlined"
                              color={rem.application_mode === 'production' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {rem.created_at ? (
                              <Tooltip title={formatDateTime(rem.created_at)}>
                                <Typography variant="body2">{formatRelativeTime(rem.created_at)}</Typography>
                              </Tooltip>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2">
                    No remittances yet. Remittance payments (835s) will appear here once payers process submitted claims.
                  </Typography>
                </Alert>
              )}

              {/* Footer */}
              {remittances.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {remittances.length} of {total} remittances
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <RemittanceDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedRemittanceId(null);
        }}
        organizationId={organizationId}
        remittanceId={selectedRemittanceId}
      />
    </Box>
  );
}
